import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getLeadFollowupSummary, getOverdueLeads, claimLeadFollowupReminder } from '../services/leadFollowupAPI';
import { useToast } from './notifications/ToastProvider';
import './LeadFollowupAlerts.css';

const ROLES = ['admin', 'administrateur', 'directeur', 'super_admin', 'conseillere', 'secretaire'];
const formatDate = value => new Date(value).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });

export default function LeadFollowupAlerts({ userRole, onOpenLead }) {
  const { showWarning } = useToast();
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(false);
  const [open, setOpen] = useState(false);
  const [owner, setOwner] = useState('');
  const [page, setPage] = useState(1);
  const [list, setList] = useState(null);
  const [listError, setListError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);
  const panel = useRef(null);
  const allowed = ROLES.includes(userRole);
  const showList = useCallback(() => { setOwner(''); setPage(1); setOpen(true); }, []);
  const refresh = useCallback(() => setReload(value => value + 1), []);

  useEffect(() => {
    if (!allowed) return undefined;
    let alive = true;
    let inFlight = false;
    const token = localStorage.getItem('token');
    setSummary(null);
    const load = async () => {
      if (localStorage.getItem('token') !== token) { setSummary(null); setOpen(false); return; }
      if (inFlight || document.visibilityState === 'hidden' || !token) return;
      inFlight = true;
      try {
        const data = await getLeadFollowupSummary();
        if (!alive || localStorage.getItem('token') !== token) return;
        setSummary(data);
        setSummaryError(false);
        if (data.total > 0) {
          try {
            const reminder = await claimLeadFollowupReminder();
            if (alive && reminder.afficher && localStorage.getItem('token') === token) {
              showWarning('Leads en attente de traitement',
                `${data.total} lead${data.total > 1 ? 's' : ''} sans traitement depuis plus de 72 h${data.administratif ? ' dans votre entreprise' : ''}.`,
                [{ label: 'Voir les leads à traiter', onClick: showList, variant: 'primary' }], 10000);
            }
          } catch (_) { /* The permanent counter remains available if the daily reminder fails. */ }
        }
      } catch (error) {
        if (alive && localStorage.getItem('token') === token) setSummaryError(true);
      } finally { inFlight = false; }
    };
    load();
    const timer = setInterval(load, 60000);
    window.addEventListener('focus', load);
    window.addEventListener('lead-followup-changed', load);
    const sessionChanged = event => {
      if (event.key === 'token' || event.key === null) { setSummary(null); setOpen(false); refresh(); }
    };
    window.addEventListener('storage', sessionChanged);
    document.addEventListener('visibilitychange', load);
    return () => {
      alive = false;
      clearInterval(timer);
      window.removeEventListener('focus', load);
      window.removeEventListener('lead-followup-changed', load);
      window.removeEventListener('storage', sessionChanged);
      document.removeEventListener('visibilitychange', load);
    };
  }, [allowed, userRole, reload, showWarning, showList, refresh]);

  useEffect(() => {
    if (!open || !allowed) return undefined;
    let alive = true;
    let inFlight = false;
    const load = async () => {
      if (inFlight) return;
      inFlight = true;
      setLoading(true);
      setListError(false);
      try {
        const data = await getOverdueLeads({ responsable_id: owner || undefined, page, limit: 20 });
        if (alive) {
          setList(data);
          if (page > Math.max(1, data.totalPages)) setPage(Math.max(1, data.totalPages));
        }
      } catch (_) { if (alive) setListError(true); }
      finally { inFlight = false; if (alive) setLoading(false); }
    };
    setList(null);
    load();
    const timer = setInterval(load, 60000);
    window.addEventListener('focus', load);
    window.addEventListener('lead-followup-changed', load);
    return () => { alive = false; clearInterval(timer); window.removeEventListener('focus', load); window.removeEventListener('lead-followup-changed', load); };
  }, [open, allowed, owner, page, reload]);

  useEffect(() => {
    if (!open) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    const keydown = event => {
      if (event.key === 'Escape') setOpen(false);
      if (event.key !== 'Tab' || !panel.current) return;
      const elements = [...panel.current.querySelectorAll('button:not(:disabled), select, a[href]')];
      const first = elements[0]; const last = elements[elements.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener('keydown', keydown); previousFocus?.focus?.(); };
  }, [open]);

  if (!allowed) return null;
  return <>
    {summaryError ? <div className="lead-followup-banner lead-followup-error" role="status">
      <span>Le suivi des leads de plus de 72 h est temporairement indisponible.</span>
      <button type="button" className="lead-followup-action" onClick={refresh}>Réessayer</button>
    </div> : summary?.total > 0 && <div className="lead-followup-banner" role="status">
      <div><strong>⚠ {summary.total} lead{summary.total > 1 ? 's' : ''} sans traitement depuis plus de 72 h</strong>
        <p>{summary.administratif ? 'Suivi des conseillères et secrétaires de votre entreprise.' : 'Un premier suivi est attendu sur ces leads.'}</p>
      </div>
      <button type="button" className="lead-followup-action" onClick={showList}>Voir les leads à traiter</button>
    </div>}
    {open && <div className="lead-followup-overlay" onClick={event => { if (event.target === event.currentTarget) setOpen(false); }}>
      <section role="dialog" aria-modal="true" aria-labelledby="lead-followup-title" className="lead-followup-panel" ref={panel} tabIndex={-1}>
        <header><div><h2 id="lead-followup-title">Leads à traiter — plus de 72 h</h2>
          <p>Les plus anciens en premier · Le délai commence à l’attribution · Week-ends compris</p></div>
          <button type="button" aria-label="Fermer les leads à traiter" onClick={() => setOpen(false)}>Fermer</button></header>
        <div className="lead-followup-toolbar">
          <strong>{list ? `${list.total} lead${list.total > 1 ? 's' : ''} en attente` : 'Chargement…'}</strong>
          {summary?.administratif && <label>Responsable
            <select aria-label="Filtrer par responsable" value={owner} onChange={event => { setOwner(event.target.value); setPage(1); }}>
              <option value="">Tous les responsables</option>
              {summary.responsables.map(item => <option key={item.responsable_id} value={item.responsable_id}>
                {item.responsable} · {item.responsable_role === 'secretaire' ? 'Secrétaire' : 'Conseillère'} ({item.nombre})
              </option>)}
            </select></label>}
          <button type="button" disabled={loading} onClick={refresh}>Actualiser</button>
        </div>
        <div className="lead-followup-table" aria-busy={loading}>
          {listError ? <p role="alert">Impossible de charger les leads. Cliquez sur Actualiser pour réessayer.</p> : <table>
            <thead><tr><th>Lead</th>{summary?.administratif && <th>Responsable</th>}<th>Attribué le</th><th>En attente depuis</th><th>Action</th></tr></thead>
            <tbody>{list?.leads.map(lead => <tr key={lead.id}>
              <td><strong>{lead.prenom} {lead.nom}</strong><br />{lead.telephone || lead.email || '—'}</td>
              {summary?.administratif && <td>{lead.responsable}<br /><small>{lead.responsable_role === 'secretaire' ? 'Secrétaire' : 'Conseillère'}</small></td>}
              <td>{formatDate(lead.date_attribution)}</td><td>{lead.heures_attente} h</td>
              <td><button type="button" className="lead-followup-action" aria-label={`Ouvrir le lead ${lead.prenom} ${lead.nom}`} onClick={() => { setOpen(false); onOpenLead(lead.id); }}>Ouvrir le lead</button></td>
            </tr>)}</tbody>
          </table>}
          {!listError && list?.total === 0 && <p className="lead-followup-empty">Aucun lead en retard pour cette sélection.</p>}
        </div>
        <footer><span>Page {page} sur {Math.max(1, list?.totalPages || 1)}</span>
          <button type="button" disabled={loading || page <= 1} onClick={() => setPage(value => value - 1)}>Précédent</button>
          <button type="button" disabled={loading || !list || page >= list.totalPages} onClick={() => setPage(value => value + 1)}>Suivant</button>
        </footer>
      </section>
    </div>}
  </>;
}
